import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
  cardedCriteria,
  readCriterionEvidence,
} from '@/features/deal-review/criterion-evidence'
import type { CriterionEvidence } from '@/features/deal-review/criterion-evidence'
import { Button } from '@/components/ui/button'
import {
  displayValue,
  evidenceKeyFor,
  evidenceQuote,
} from '@/features/deal-review/deal-lead-fields'
import { InlineFieldValue } from '@/components/custom/deal-review/inline-field'
import { correctableFor } from '@/features/deal-review/deal-lead-corrections'
import type { CorrectableField } from '@/features/deal-review/deal-lead-corrections'
import { useScreenAgain } from '@/hooks/use-screen-again'
import { CriterionMark } from '@/components/custom/deal-review/panel-primitives'
import type { CriterionTone } from '@/components/custom/deal-review/panel-primitives'
import {
  fieldLabel,
  ruleLabel,
  screeningOutcomeLabel,
} from '@/features/deal-review/deal-lead-labels'
import { useCriterionLabels } from '@/hooks/use-criterion-labels'
import type { DealLeadDetail } from '@/types/api'

type ScreeningRun = NonNullable<DealLeadDetail['latestScreeningRun']>
type CriterionResult = ScreeningRun['criterionResults'][number]
type CriterionOutcome = ScreeningRun['coreResult']

const TONE_FOR: Record<CriterionOutcome, CriterionTone> = {
  PASS: 'met',
  FAIL: 'not-met',
  UNKNOWN: 'missing',
  NOT_APPLICABLE: 'not-applicable',
}

const BAND_ORDER: CriterionOutcome[] = [
  'FAIL',
  'UNKNOWN',
  'PASS',
  'NOT_APPLICABLE',
]

const BAND_TITLES: Record<CriterionOutcome, string> = {
  FAIL: 'Failed',
  UNKNOWN: 'Could not be decided',
  PASS: 'Met',
  NOT_APPLICABLE: 'Not applicable',
}

const BAND_CLASSES: Record<CriterionOutcome, string> = {
  FAIL: 'text-destructive',
  UNKNOWN: 'text-amber-900',
  PASS: 'text-emerald-800',
  NOT_APPLICABLE: 'text-primary/70',
}

const BAND_STRIP: Record<CriterionOutcome, string> = {
  FAIL: 'bg-red-50',
  UNKNOWN: 'bg-amber-50',
  PASS: 'bg-emerald-50',
  NOT_APPLICABLE: 'bg-primary/5',
}

const VALUE_CLASSES: Record<CriterionOutcome, string> = {
  FAIL: 'text-destructive',
  UNKNOWN: 'text-amber-900',
  PASS: 'text-foreground',
  NOT_APPLICABLE: 'text-foreground',
}

const COLUMNS =
  'grid grid-cols-[20px_minmax(0,1.45fr)_minmax(0,1.15fr)_minmax(0,1.1fr)] gap-3'

function fieldOf(evidence: CriterionEvidence | null): string | null {
  return evidence?.kind === 'rule' ? fieldLabel(evidence.field) : null
}

function valueRead(
  outcome: CriterionOutcome,
  evidence: CriterionEvidence | null
): string {
  if (outcome === 'UNKNOWN') return 'Not in the source'
  if (evidence?.kind === 'rule') {
    return evidence.found === 'not stated' ? 'Not stated' : evidence.found
  }
  if (evidence?.kind === 'quotation') return 'Judged from the text'
  return 'Nothing quoted'
}

function rowExplanation(
  result: CriterionResult,
  evidence: CriterionEvidence | null
): string | null {
  if (result.outcome !== 'PASS' || !result.explanation) return null
  if (evidence?.kind === 'rule' && evidence.found !== 'no excluded tag') {
    return null
  }
  return result.explanation
}

function limitRead(
  outcome: CriterionOutcome,
  evidence: CriterionEvidence | null
): string {
  if (outcome === 'UNKNOWN') return 'needed to decide'
  if (evidence?.kind === 'rule') return evidence.required
  if (outcome === 'NOT_APPLICABLE') return 'not applicable'
  return 'no fixed threshold'
}

function evidenceLine(
  outcome: CriterionOutcome,
  evidence: CriterionEvidence | null
): string | null {
  if (evidence === null) return null
  if (evidence.kind === 'quotation') return evidence.quotation

  const found =
    outcome === 'UNKNOWN' ? 'not in the source' : `found ${evidence.found}`
  return `${ruleLabel(evidence.rule)} · ${fieldLabel(evidence.field)} — required ${evidence.required}, ${found}`
}

// Only tag rules classify; every other rule compares an extracted value.
function classificationRationale(
  lead: DealLeadDetail,
  outcome: CriterionOutcome,
  evidence: CriterionEvidence | null
): string | null {
  if (outcome !== 'FAIL' || evidence?.kind !== 'rule') return null
  if (evidence.rule !== 'TAG_DENY') return null

  const read = `${fieldLabel(evidence.field)} was read as ${evidence.found}`
  const quote = evidenceQuote(lead, evidenceKeyFor(evidence.field))
  return quote === null
    ? `${read}. No excerpt was captured for this classification.`
    : `${read} from: “${quote}”`
}

// Rules only: a model-judged criterion has no single field, and tag axes aren't corrected in place.
function fillableField(
  lead: DealLeadDetail,
  outcome: CriterionOutcome,
  evidence: CriterionEvidence | null
): CorrectableField | null {
  if (outcome !== 'UNKNOWN' || evidence?.kind !== 'rule') return null
  if (!lead.capabilities.canCorrect) return null
  return correctableFor(evidence.field)
}

export function CriteriaTable({
  run,
  lead,
}: {
  run: ScreeningRun
  lead: DealLeadDetail
}) {
  const labels = useCriterionLabels()
  const filled = lead.capabilities.canCorrect
    ? run.fieldsFilledSinceScreening.length
    : 0

  if (run.criterionResults.length === 0) {
    return (
      <Typography variant="body-sm" className="italic text-primary/70">
        This run reported no criteria.
      </Typography>
    )
  }

  const bands = BAND_ORDER.filter((outcome) =>
    run.criterionResults.some((result) => result.outcome === outcome)
  )

  return (
    <div>
      {filled > 0 && <ScreenAgainPrompt lead={lead} filled={filled} />}

      <div
        className={cn(
          COLUMNS,
          'border-b border-primary/15 pb-2 text-[10.5px] font-medium uppercase leading-none tracking-[0.08em] text-primary/70'
        )}
      >
        <div />
        <div>Check</div>
        <div className="text-right">Value read</div>
        <div>Has to be</div>
      </div>

      {bands.map((outcome, index) => (
        <CriterionBand
          key={outcome}
          outcome={outcome}
          results={run.criterionResults.filter(
            (result) => result.outcome === outcome
          )}
          labels={labels}
          lead={lead}
          first={index === 0}
        />
      ))}
    </div>
  )
}

// Filling a value never re-screens on its own; one offer covers every filled field.
function ScreenAgainPrompt({
  lead,
  filled,
}: {
  lead: DealLeadDetail
  filled: number
}) {
  const screen = useScreenAgain(lead.id)

  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-secondary/25 bg-secondary/5 px-3 py-2">
      <Typography variant="body-sm" className="text-primary">
        {filled === 1 ? '1 field' : `${filled} fields`} filled since screening.
        The checks below still show the last run.
      </Typography>
      <Button
        size="sm"
        variant="outline"
        className="shrink-0"
        disabled={screen.isPending}
        onClick={screen.run}
      >
        {screen.isPending ? 'Screening…' : 'Screen again'}
      </Button>
    </div>
  )
}

// The toggle names the total, so a capped band never reads as everything that was checked.
const BAND_LIMIT = 5

function CriterionBand({
  outcome,
  results,
  labels,
  lead,
  first,
}: {
  outcome: CriterionOutcome
  results: CriterionResult[]
  labels: Map<string, string>
  lead: DealLeadDetail
  first: boolean
}) {
  const [expanded, setExpanded] = useState(false)

  // Failures are never capped: they are the argument for passing on the lead.
  const capped = outcome !== 'FAIL' && !expanded && results.length > BAND_LIMIT
  const shown = capped ? results.slice(0, BAND_LIMIT) : results

  return (
    <section>
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 rounded-md py-1.5 pl-8 pr-3',
          BAND_STRIP[outcome],
          first ? 'mt-3' : 'mt-6'
        )}
      >
        <Typography
          variant="body-sm"
          className={cn(
            'text-[10.5px] font-bold uppercase leading-none tracking-wider',
            BAND_CLASSES[outcome]
          )}
        >
          {BAND_TITLES[outcome]}
        </Typography>
        <Typography
          variant="body-sm"
          className={cn(
            'text-xs leading-none tabular-nums opacity-80',
            BAND_CLASSES[outcome]
          )}
        >
          {results.length}
        </Typography>
      </div>

      {shown.map((result, index) => (
        <CriterionRow
          key={result.criterionId}
          result={result}
          outcome={outcome}
          title={labels.get(result.criterionId) ?? result.criterionId}
          lead={lead}
          first={index === 0}
        />
      ))}

      {outcome !== 'FAIL' && results.length > BAND_LIMIT && (
        <button
          type="button"
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1.5 border-t border-primary/10 py-2.5 text-xs font-medium text-secondary hover:bg-secondary/5"
          onClick={() => setExpanded((open) => !open)}
        >
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-3 transition-transform',
              expanded && 'rotate-180'
            )}
          />
          {expanded ? 'Show fewer' : `Show ${results.length - BAND_LIMIT} more`}
        </button>
      )}
    </section>
  )
}

function CriterionRow({
  result,
  outcome,
  title,
  lead,
  first,
}: {
  result: CriterionResult
  outcome: CriterionOutcome
  title: string
  lead: DealLeadDetail
  first: boolean
}) {
  const evidence = readCriterionEvidence(result.evidence)
  const field = fieldOf(evidence)
  const explanation = rowExplanation(result, evidence)
  const fillable = fillableField(lead, outcome, evidence)

  return (
    <div
      className={cn(
        COLUMNS,
        'items-baseline py-2.5',
        first ? '' : 'border-t border-primary/10'
      )}
    >
      <CriterionMark
        tone={TONE_FOR[outcome]}
        label={screeningOutcomeLabel(outcome)}
      />
      <div>
        <Typography variant="body-sm" className="text-xs text-primary/90">
          {field ?? title}
        </Typography>
        {field !== null && (
          <Typography
            variant="body-sm"
            className="mt-0.5 text-xs text-primary/70"
          >
            {title}
          </Typography>
        )}
        {explanation && (
          <Typography
            variant="body-sm"
            title={explanation}
            className="mt-0.5 line-clamp-2 text-xs leading-[1.4] text-primary/70"
          >
            {explanation}
          </Typography>
        )}
      </div>
      {fillable ? (
        <FillValue lead={lead} field={fillable} />
      ) : (
        <Typography
          variant="body-sm"
          className={cn(
            'break-words text-right text-xs leading-[1.2] tabular-nums',
            VALUE_CLASSES[outcome]
          )}
        >
          {valueRead(outcome, evidence)}
        </Typography>
      )}
      <Typography
        variant="body-sm"
        className="line-clamp-2 break-words text-xs leading-[1.4] tabular-nums text-primary/70"
      >
        {limitRead(outcome, evidence)}
      </Typography>
    </div>
  )
}

// The outcome beside a filled value is still the last run's.
function FillValue({
  lead,
  field,
}: {
  lead: DealLeadDetail
  field: CorrectableField
}) {
  const filled = displayValue(lead, field.key)
  const unscreened =
    lead.latestScreeningRun?.fieldsFilledSinceScreening.includes(field.key) ??
    false

  return (
    <div className="flex flex-col items-end text-right">
      <InlineFieldValue
        lead={lead}
        field={field}
        display={filled ?? 'Add value'}
        className={cn(
          'text-xs leading-[1.2] tabular-nums',
          filled ? 'text-foreground' : 'text-secondary'
        )}
      />
      {unscreened && (
        <Typography
          variant="body-sm"
          className="mt-0.5 text-xs text-primary/70"
        >
          Not yet screened
        </Typography>
      )}
    </div>
  )
}

export function OpenQuestions({
  run,
  lead,
  withEvidence,
}: {
  run: ScreeningRun
  lead: DealLeadDetail
  withEvidence: boolean
}) {
  const labels = useCriterionLabels()
  const [expanded, setExpanded] = useState(false)
  const open = cardedCriteria(run)

  if (open.length === 0) return null

  // Failures are never hidden: cardedCriteria returns only those whenever there are any.
  const capped = !expanded && open.length > CARD_LIMIT
  const shown = capped ? open.slice(0, CARD_LIMIT) : open

  return (
    <div className="flex flex-col gap-2">
      {shown.map((result) => {
        const failed = result.outcome === 'FAIL'
        const evidence = readCriterionEvidence(result.evidence)
        const detail =
          classificationRationale(lead, result.outcome, evidence) ??
          evidenceLine(result.outcome, evidence)

        return (
          <div
            key={result.criterionId}
            className={cn(
              'rounded-lg border px-3.5 py-3',
              failed
                ? 'border-destructive/25 bg-destructive/[0.04] text-destructive'
                : 'border-amber-700/25 bg-amber-50 text-amber-900'
            )}
          >
            <Typography variant="body" className="text-sm font-bold">
              {labels.get(result.criterionId) ?? result.criterionId}
            </Typography>
            {result.explanation && (
              <Typography
                variant="body-sm"
                className="mt-1 max-w-[62ch] leading-[1.55]"
              >
                {result.explanation}
              </Typography>
            )}
            {withEvidence && detail && (
              <Typography
                variant="body-sm"
                className="mt-2 max-w-[62ch] italic leading-normal opacity-80"
              >
                {detail}
              </Typography>
            )}
          </div>
        )
      })}

      {open.length > CARD_LIMIT && (
        <button
          type="button"
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-secondary hover:bg-secondary/5"
          onClick={() => setExpanded((isOpen) => !isOpen)}
        >
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-3 transition-transform',
              expanded && 'rotate-180'
            )}
          />
          {expanded ? 'Show fewer' : `Show ${open.length - CARD_LIMIT} more`}
        </button>
      )}
    </div>
  )
}

const CARD_LIMIT = 4
